import { z } from "zod";

// ── Address shape (shared by domain & form) ───────────────────────────────────

export const VendorAddressSchema = z.object({
  line1: z.string().trim().max(255).nullable(),
  line2: z.string().trim().max(255).nullable(),
  city: z.string().trim().max(100).nullable(),
  state: z.string().trim().max(100).nullable(),
  postalCode: z.string().trim().max(20).nullable(),
  country: z.string().trim().max(100).nullable(),
});

export type VendorAddress = z.infer<typeof VendorAddressSchema>;

// ── Domain schema (DB / server truth) ─────────────────────────────────────────

export const VendorSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  name: z.string(),
  contactName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  website: z.string().optional(),
  address: VendorAddressSchema.nullable().optional(),
  notes: z.string().optional(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});

export type Vendor = z.infer<typeof VendorSchema>;

export const CreateVendorSchema = VendorSchema.omit({
  id: true,
  businessId: true,
  createdAt: true,
  updatedAt: true,
});

export const UpdateVendorSchema = CreateVendorSchema.partial();

export type CreateVendorInput = z.infer<typeof CreateVendorSchema>;
export type UpdateVendorInput = z.infer<typeof UpdateVendorSchema>;

// ── Form schema (UI layer) ────────────────────────────────────────────────────

export const VendorFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  contactName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  website: z.string().optional(),
  address: z.object({
    line1: z.string().max(255, "Must be 255 characters or less"),
    line2: z.string().max(255, "Must be 255 characters or less"),
    city: z.string().max(100, "Must be 100 characters or less"),
    state: z.string().max(100, "Must be 100 characters or less"),
    postalCode: z.string().max(20, "Must be 20 characters or less"),
    country: z.string().max(100, "Must be 100 characters or less"),
  }),
  notes: z.string().optional(),
});

export type VendorForm = z.infer<typeof VendorFormSchema>;

// ── Mapping: Form → Domain ────────────────────────────────────────────────────

const emptyToNull = (v: string): string | null =>
  v.trim() === "" ? null : v.trim();

export function formToDomain(input: VendorForm): UpdateVendorInput {
  return {
    name: input.name,
    contactName: input.contactName || undefined,
    email: input.email || undefined,
    phone: input.phone || undefined,
    website: input.website || undefined,
    address: {
      line1: emptyToNull(input.address.line1),
      line2: emptyToNull(input.address.line2),
      city: emptyToNull(input.address.city),
      state: emptyToNull(input.address.state),
      postalCode: emptyToNull(input.address.postalCode),
      country: emptyToNull(input.address.country),
    },
    notes: input.notes || undefined,
  };
}

export function domainToForm(item: Vendor): VendorForm {
  return {
    name: item.name,
    contactName: item.contactName ?? "",
    email: item.email ?? "",
    phone: item.phone ?? "",
    website: item.website ?? "",
    address: {
      line1: item.address?.line1 ?? "",
      line2: item.address?.line2 ?? "",
      city: item.address?.city ?? "",
      state: item.address?.state ?? "",
      postalCode: item.address?.postalCode ?? "",
      country: item.address?.country ?? "",
    },
    notes: item.notes ?? "",
  };
}
